import { gql } from "@apollo/client";
import CookieManager from "@/services/CookieManager";
import { batch } from "react-redux";
import { captureException, configureScope } from "@sentry/browser";
import config from "../config/config";
import { AppState } from "../reducers";
import { parseError } from "../services/Error";
import Tracker from "../services/Tracker";
import { getCustomerProfile } from "./account";
import { apollo, AppDispatch, AppThunk, formHandleInput } from "./index";
import dayjs from "dayjs";
import {
  AUTH_USER_SET,
  AUTH_LOGIN_ERROR,
  AUTH_LOGIN_LOADING,
  AUTH_LOSTPASSWORD_MAIL_SENT,
  AUTH_LOSTPASSWORD_SET_AUTHORIZATION,
  AUTH_LOSTPASSWORD_LOADING,
  AUTH_LOSTPASSWORD_ERROR,
  AUTH_CHANGE_BADGE_SUCCESS,
  AUTH_CHANGE_BADGE_ERROR,
  AUTH_CHANGE_BADGE_RESET,
  APP_LOADING_END,
  APP_LOADING_START,
  AUTH_DELETEACCOUNT_ERROR,
  AUTH_RESET_STATE,
  ACCOUNT_RESET_STATE,
  WALLET_RESET_STATE,
} from "./types";

import * as rootNavigation from "../navigators/RootNavigatorRef";
import { addToast } from "./toast";
import { Platform } from "react-native";
import SegmentClient from "../helpers/SegmentClient";
import type { TranslateOptions } from "@/i18n";
import { translateMessage } from "@/helpers/translation";

const translateAuthMessage = (key: string, options?: TranslateOptions) =>
  translateMessage(`auth.${key}`, options);

const extractTokenFromUrl = (maybeUrl?: string): string | undefined => {
  if (!maybeUrl) return undefined;

  try {
    const urlToParse = maybeUrl.includes("://") ? maybeUrl : `https://${maybeUrl}`;
    const parsed = new URL(urlToParse);
    const token = parsed.searchParams.get("token");
    if (token) {
      return token;
    }
  } catch {
    // ignore parse errors and try fallback parsing below
  }

  const queryIndex = maybeUrl.indexOf("?");
  if (queryIndex === -1) {
    return undefined;
  }

  const params = new URLSearchParams(maybeUrl.slice(queryIndex + 1));
  return params.get("token") ?? undefined;
};

export const checkTokenFromCookieThenAuthenticate =
  (redirect?: string): AppThunk<Promise<void>> =>
  async (dispatch: AppDispatch) => {
    const tokenFromUrl = extractTokenFromUrl(redirect);
    const sanitizedRedirect = redirect?.split("?")[0] || undefined;

    if (tokenFromUrl) {
      await setTokenToCookie(tokenFromUrl);
      await dispatch(getUserFromToken(tokenFromUrl, sanitizedRedirect));
      return;
    }

    const token = await getTokenFromCookie();

    if (token) {
      await dispatch(getUserFromToken(token, sanitizedRedirect));
    } else {
      await dispatch({ type: APP_LOADING_END });
    }
  };

export const authenticate =
  ({ redirect }: { redirect?: string | null } = {}): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: AUTH_LOGIN_LOADING });
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    const { form } = getState();
    const { email, password } = form.login;
    const type = "Canteen";
    const accountNotFoundMessage = translateAuthMessage("login.accountNotFound", {
      defaultValue: "Aucun compte n'existe pour cette adresse email.",
    });
    const invalidPasswordMessage = translateAuthMessage("login.invalidPassword", {
      defaultValue: "Le mot de passe est incorrect.",
    });

    try {
      const result: any = await apollo().mutate<any>({
        variables: { email, password, type },
        mutation: gql`
          mutation ($email: String!, $password: String!, $type: String!) {
            authenticate(email: $email, password: $password, type: $type) {
              token
            }
          }
        `,
      });
      if (result.data) {
        const {
          data: {
            authenticate: { token },
          },
        } = result;

        setTokenToCookie(token).then(async () => {
          await dispatch(getUserFromToken(token));
          rootNavigation.resetAndGoTo(redirect || "home");
        });
      } else if (result.errors && result.errors.length > 0) {
        if (result.errors[0]?.message?.includes("Unknown account")) {
          SegmentClient.track("Log In Failed", {
            message: accountNotFoundMessage,
          });
          dispatch({
            type: AUTH_LOGIN_ERROR,
            message: accountNotFoundMessage,
          });
        } else {
          captureException(result.errors[0]);
          SegmentClient.track("Log In Failed", {
            message: invalidPasswordMessage,
          });
          dispatch({
            type: AUTH_LOGIN_ERROR,
            message: invalidPasswordMessage,
          });
        }
      }
    } catch (err) {
      console.error("err login", parseError(String(err)));
      if (parseError(String(err)).includes("Unknown account")) {
        dispatch({
          type: AUTH_LOGIN_ERROR,
          message: accountNotFoundMessage,
        });
      } else {
        captureException(err);

        dispatch({
          type: AUTH_LOGIN_ERROR,
          message: invalidPasswordMessage,
        });
      }
    } finally {
      dispatch({ type: APP_LOADING_END });
    }
  };

export const getUserFromToken =
  (token: string, redirect?: string): AppThunk<Promise<void>> =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    try {
      const req: any = await apollo().query({
        variables: { token },
        query: gql`
          query ($token: String!) {
            getUserFromToken(token: $token) {
              id
              firstname
              lastname
              email
              phone
              token
              type
              badgeId
              hasBadgeIdBeenGenerated
            }
          }
        `,
      });

      const userFromToken = req.data.getUserFromToken;

      dispatch({
        type: AUTH_USER_SET,
        user: userFromToken,
      });
      await dispatch(getCustomerProfile());
      setTokenToCookie(token);
    } catch (err) {
      captureException(err);
      //if (parseError(String(error)) === "Error:   Wrong Token") {
      await removeTokenFromCookie();
      dispatch({
        type: AUTH_LOGIN_ERROR,
        message: `Un problème de connexion est survenu, veuillez vous reconnecter.`,
      });
      //}
    }
  };

export const refreshUserFromToken = (): AppThunk<Promise<void>> => async dispatch => {
  const token = await getTokenFromCookie();

  if (token) {
    await apollo().cache.reset();

    try {
      const req: any = await apollo().query({
        variables: { token },
        query: gql`
          query {
            refreshUserFromToken {
              id
              firstname
              lastname
              email
              phone
              token
              type
              badgeId
              hasBadgeIdBeenGenerated
            }
          }
        `,
      });

      const userFromToken = req.data.refreshUserFromToken;

      dispatch({
        type: AUTH_USER_SET,
        user: userFromToken ?? null,
      });
    } catch (err) {
      captureException(err);
      await removeTokenFromCookie();
      dispatch({
        type: AUTH_LOGIN_ERROR,
        message: translateAuthMessage("login.connectionError", {
          defaultValue: "Un problème de connexion est survenu, veuillez vous reconnecter.",
        }),
      });
    }
  }
};

export const logout = (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  try {
    await removeTokenFromCookie();
    const {
      auth: {
        user: { token },
      },
    } = getState();

    await apollo().mutate<any>({
      variables: { token },
      mutation: gql`
        mutation ($token: String!) {
          canteenDisconnect(token: $token)
        }
      `,
    });

    dispatch({ type: AUTH_RESET_STATE });
    dispatch({ type: ACCOUNT_RESET_STATE });
    dispatch({ type: WALLET_RESET_STATE });

    rootNavigation.resetAndGoTo("onBoarding");

    try {
      Tracker.track("Button Log Out Clicked");
    } catch {
      // ignore
    }
  } catch (err) {
    console.error("err logout", parseError(String(err)));
    captureException(err);
  }
};

export const retrievePassword =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: AUTH_LOSTPASSWORD_LOADING });

    const type = "Canteen";
    const { form } = getState();
    const { email } = form.lostPassword;
    try {
      const req = await apollo().mutate<any>({
        variables: { email, type },
        mutation: gql`
          mutation ($email: String!, $type: String!) {
            retrievePassword(email: $email, type: $type)
          }
        `,
      });

      if (req.data.retrievePassword === true) {
        dispatch({ type: AUTH_LOSTPASSWORD_MAIL_SENT });
      } else {
        dispatch({ type: AUTH_LOSTPASSWORD_ERROR });
      }
    } catch (err) {
      captureException(err);
    }
  };

export const checkTokenBeforeResettingPassword =
  (token: string, type: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: AUTH_LOSTPASSWORD_LOADING });

    try {
      const {
        data: { checkTokenValidity },
      } = await apollo().mutate<any>({
        variables: { token, type },
        mutation: gql`
          mutation ($token: String!, $type: String!) {
            checkTokenValidity(token: $token, type: $type)
          }
        `,
      });

      dispatch({
        type: AUTH_LOSTPASSWORD_SET_AUTHORIZATION,
        authorized: checkTokenValidity,
      });
    } catch (err) {
      captureException(err);
    }
  };

export const submitNewPassword =
  (token: string, password: string): any =>
  async (dispatch: AppDispatch) => {
    // Anonymous reset password (when password is lost, by token)
    const passwordResetSuccessMessage = translateAuthMessage("password.resetSuccess", {
      defaultValue: "Votre mot de passe a bien été modifié",
    });
    const passwordResetErrorMessage = translateAuthMessage("password.resetError", {
      defaultValue:
        "Une erreur est survenue lors de la modification de votre mot de passe. Votre demande de changement de mot de passe a peut-être expiré.",
    });
    try {
      const result = await apollo().mutate<any>({
        variables: { token, password },
        mutation: gql`
          mutation ($token: String!, $password: String!) {
            setNewPassword(token: $token, password: $password)
          }
        `,
      });
      if (result.data?.setNewPassword === true) {
        dispatch(
          addToast({
            type: "success",
            message: passwordResetSuccessMessage,
          }),
        );
        rootNavigation.navigate("loginModal");
      } else if (result.errors && result.errors.length > 0) {
        dispatch(
          addToast({
            type: "error",
            message: passwordResetErrorMessage,
          }),
        );
      }
    } catch (err) {
      captureException(err);
    }
  };

export const getTokenFromCookie = (): Promise<string | undefined | void> => {
  return CookieManager.get(config.cookies.url)
    .then((cookies: any) => {
      return cookies[config.cookies.authName]?.value as string | undefined;
    })
    .catch((err: any) => {
      console.error("Error getting cookie", err);
      return undefined;
    });
};

export const setTokenToCookie = async (token: string): Promise<boolean> => {
  return CookieManager.set(config.cookies.url, {
    name: config.cookies.authName,
    value: token,
    path: "/",
    version: "1",
    expires: dayjs().add(config.cookies.expires, "days").format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
  });
};

export const removeTokenFromCookie = async () => {
  if (Platform.OS === "ios") {
    await CookieManager.clearByName(config.cookies.url, config.cookies.authName);
  } else {
    await CookieManager.clearAll();
  }
};

export const hasUserSubscribedToNewsletter = (): any => async (dispatch: AppDispatch) => {
  try {
    const req: any = await apollo().query({
      query: gql`
        query {
          getUserMailSubscriptions {
            id
          }
        }
      `,
    });

    const getUserMailSubscriptions = req.data.getUserMailSubscriptions;

    if (
      getUserMailSubscriptions.some(
        (subscription: { id: string }) => subscription.id === config.mailjet.canteenNewsletterId,
      )
    ) {
      dispatch(formHandleInput("accountInformations", "hasAcceptedNewsletter", true));
    } else {
      dispatch(formHandleInput("accountInformations", "hasAcceptedNewsletter", false));
    }
  } catch (err) {
    captureException(err);
  }
};

export const logUserWithSentry =
  (email: string, firstname: string, lastname: string, id: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    configureScope(scope => {
      scope.setUser({
        email,
        firstname,
        lastname,
        id,
      });
    });
  };

export const changeBadge = (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  dispatch({ type: APP_LOADING_START });

  await apollo().cache.reset();

  const form = getState().form.tokenBadgeChange;
  const badgeToken =
    String(form.badgeToken1) +
    String(form.badgeToken2) +
    String(form.badgeToken3) +
    String(form.badgeToken4);
  const badgeSuccessMessage = translateAuthMessage("badge.success", {
    defaultValue: "Votre nouveau badge a bien été appairé.",
  });
  const badgeInvalidMessage = translateAuthMessage("badge.invalid", {
    defaultValue:
      "Le badge que vous avez saisi semble incorrect, veuillez vérifier votre saisie puis réessayer.",
  });

  try {
    const req = await apollo().mutate<any>({
      variables: { token: badgeToken },
      mutation: gql`
        mutation ($token: String!) {
          changeCustomerBadge(token: $token)
        }
      `,
    });

    if (req?.data?.changeCustomerBadge) {
      await dispatch(refreshUserFromToken());
      await dispatch(getCustomerProfile(false));

      dispatch({ type: AUTH_CHANGE_BADGE_SUCCESS });
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "success",
          message: badgeSuccessMessage,
        }),
      );

      batch(() => {
        dispatch(formHandleInput("tokenBadgeChange", "badgeToken1", ""));
        dispatch(formHandleInput("tokenBadgeChange", "badgeToken2", ""));
        dispatch(formHandleInput("tokenBadgeChange", "badgeToken3", ""));
        dispatch(formHandleInput("tokenBadgeChange", "badgeToken4", ""));
      });
      rootNavigation.navigate("profileDetail");
      return true;
    } else {
      dispatch({ type: AUTH_CHANGE_BADGE_ERROR });
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "error",
          message: badgeInvalidMessage,
        }),
      );

      return false;
    }
  } catch (err) {
    captureException(err);
    dispatch({ type: AUTH_CHANGE_BADGE_ERROR });
    dispatch({ type: APP_LOADING_END });
    dispatch(
      addToast({
        type: "error",
        message: translateAuthMessage("badge.error", {
          defaultValue:
            "Un problème technique est survenu pendant l'appairage de votre badge, veuillez réessayer. Si le problème persiste, veuillez contacter le support Popchef.",
        }),
      }),
    );

    return false;
  }
};

export const resetBadgeChange = (): AppThunk => (dispatch: AppDispatch) => {
  dispatch({ type: AUTH_CHANGE_BADGE_RESET });
};

export const deleteAccount =
  (): any =>
  async (dispatch: AppDispatch, getState: () => AppState): Promise<void> => {
    const { auth } = getState();
    const deleteSuccessMessage = translateAuthMessage("delete.success", {
      defaultValue: "Votre compte a bien été supprimé.",
    });
    const deleteNegativeBalanceMessage = translateAuthMessage("delete.negativeBalance", {
      defaultValue: "Le solde de votre compte est débiteur, veuillez vérifier puis réessayez.",
    });
    const deleteErrorMessage = translateAuthMessage("delete.error", {
      defaultValue:
        "Un problème technique est survenu pendant la désinscription, veuillez réessayer. Si le problème persiste, veuillez contacter le support Popchef.",
    });

    if (auth.deleteAccount.loading) {
      // Already in progress
      return;
    }

    dispatch({ type: APP_LOADING_START });

    try {
      const {
        auth: {
          user: { token },
        },
      } = getState();

      const res = await apollo().mutate<any>({
        variables: { token },
        mutation: gql`
          mutation ($token: String!) {
            deleteAccount(token: $token)
          }
        `,
      });

      if (res?.data?.deleteAccount) {
        await removeTokenFromCookie();
        dispatch({ type: AUTH_USER_SET, user: null });
        dispatch(
          addToast({
            type: "success",
            message: deleteSuccessMessage,
          }),
        );

        rootNavigation.resetAndGoTo("onBoarding");
      } else {
        throw new Error();
      }
    } catch (err) {
      if (err instanceof Error && err?.message === "WalletValueIsNegative") {
        dispatch(
          addToast({
            type: "error",
            message: deleteNegativeBalanceMessage,
          }),
        );
        dispatch({
          type: AUTH_DELETEACCOUNT_ERROR,
          errorMessage: deleteNegativeBalanceMessage,
        });
      } else {
        captureException(err);

        console.error("err deleteAccount", parseError(String(err)));
        dispatch(
          addToast({
            type: "error",
            message: deleteErrorMessage,
          }),
        );
        dispatch({
          type: AUTH_DELETEACCOUNT_ERROR,
          errorMessage: deleteErrorMessage,
        });
      }
    }
    dispatch({ type: APP_LOADING_END });
  };
